import translations from "../utilities/translations";
import flatpickr from "flatpickr";

export interface Field {
  type: string;
  rootElement: Element;
  inputElement: HTMLInputElement | null;
  required: boolean;
  fakeInputElement: HTMLElement | null;
  fakeValueElement: HTMLElement | null;
  fakeOptionElements: () => Element[];
  listbox: HTMLElement | null;
  errorElement: Element | null;
  wrapperElement: Element | null;
}

export interface Option {
  value: string;
  label: string;
}

const RFC_5322_VALIDATION =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** Apply or remove the filled state on a field. */
const toggleFilledState = (field: Field): boolean =>
  field.rootElement.classList.toggle(
    "is-filled",
    field.inputElement?.value !== undefined &&
      field.inputElement.value.length > 0
  );

/** Show the error message for a field if it's invalid.  */
const showErrorMessage = (field: Field): boolean => {
  const input =
    field.type === "date"
      ? field.rootElement.querySelector<HTMLInputElement>(
          ".js-field-input.input"
        )
      : field.inputElement;
  if (field.errorElement) {
    field.errorElement.innerHTML = input?.validationMessage || "";
    field.rootElement.classList.toggle(
      "has-error",
      input?.validity.valid === false
    );
  }

  return true;
};

/** Attach all listeners for a field. */
const attachListeners = (field: Field): Field => {
  ["input", "invalid", "change"].forEach((event) =>
    field.inputElement?.addEventListener(event, () => {
      showErrorMessage(field);
      toggleFilledState(field);
    })
  );

  // Handle any values present on page load too.
  toggleFilledState(field);

  if (field.inputElement?.value) {
    showErrorMessage(field);
  }

  // Handle dropdown interaction.
  if (field.type === "select") {
    // Toggle dropdown open state when clicking input.
    field.fakeInputElement?.addEventListener("click", () => {
      if (field.rootElement.classList.toggle("is-open") === true) {
        field.fakeInputElement?.setAttribute("aria-expanded", "true");
      } else {
        field.fakeInputElement?.setAttribute("aria-expanded", "false");
        field.fakeInputElement?.blur();
      }
    });

    window.addEventListener("click", (event) => {
      // Close the dropdown when a user clicks anywhere except the input.
      if (event.target !== field.fakeInputElement) {
        field.rootElement.classList.remove("is-open");
        field.fakeInputElement?.setAttribute("aria-expanded", "false");
      }

      // Update the field value if a user clicks a fake field option.
      if (
        event.target instanceof HTMLElement &&
        field.inputElement &&
        field.fakeOptionElements().includes(event.target) &&
        event.target.dataset.value
      ) {
        field.inputElement.value = event.target.dataset.value;
        // Keyup ensures Freeform conditional rules are triggered.
        field.inputElement.dispatchEvent(new Event("keyup"));
        field.inputElement.dispatchEvent(new Event("change"));
        toggleFilledState(field);
        showErrorMessage(field);

        if (field.fakeValueElement) {
          field.fakeValueElement.innerText = event.target.innerText.trim();
        }

        event.target
          .closest('[role="listbox"]')
          ?.setAttribute("aria-activedescendant", event.target.id);
      }
    });
  }

  // Handle file interaction.
  if (field.type === "file") {
    const fileNameDisplay = field.rootElement.querySelector(
      ".js-field-file-name"
    ) as HTMLElement;
    const checkmarkTemplate = field.rootElement.querySelector(
      ".js-checkmark-template"
    ) as HTMLTemplateElement;

    field.fakeInputElement?.addEventListener("click", () =>
      field.inputElement?.click()
    );

    // When a file is selected, display its name
    field.inputElement?.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;

      if (input.files && input.files.length > 0) {
        // Clear previous file names
        fileNameDisplay.innerHTML = "";

        // Create a list of file names
        Array.from(input.files).forEach((file) => {
          const fileItem = document.createElement("div");
          fileItem.className = "field__file-item";

          // Check if the file is an image
          const isImage = file.type.startsWith("image/");

          // If it's an image, add a thumbnail
          if (isImage) {
            const thumbnail = document.createElement("img");
            thumbnail.alt = file.name;

            const fileUrl = URL.createObjectURL(file);
            thumbnail.src = fileUrl;

            thumbnail.onload = () => {
              URL.revokeObjectURL(fileUrl);
            };

            fileItem.appendChild(thumbnail);
          } else {
            // For non-image files, add a generic file icon
            const fileIcon = document.createElement("div");
            fileIcon.className = "field__file-icon";
            fileIcon.innerHTML =
              '<svg class="w-24 h-24 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" /></svg>';
            fileItem.appendChild(fileIcon);
          }

          const fileNameSpan = document.createElement("span");
          fileNameSpan.className = "mr-2";
          fileNameSpan.textContent = "File name: " + file.name;
          fileItem.appendChild(fileNameSpan);

          // Add checkmark template
          if (checkmarkTemplate) {
            const checkmarkClone = checkmarkTemplate.content.cloneNode(true);
            fileItem.appendChild(checkmarkClone);
          }

          fileNameDisplay.appendChild(fileItem);
        });

        fileNameDisplay.classList.remove("hidden");
        fileNameDisplay.classList.add("flex");
      } else {
        fileNameDisplay.innerHTML = "";
        fileNameDisplay.classList.add("hidden");
        fileNameDisplay.classList.remove("flex");
      }
    });
  }

  // Handle date interaction.
  if (field.type === "date" && field.inputElement) {
    const picker = flatpickr(field.inputElement, {
      altInput: true,
      altFormat: "F j, Y",
      dateFormat: "Y-m-d",
      allowInput: true,
    });
    // See: https://github.com/flatpickr/flatpickr/issues/892#issuecomment-604387030
    picker._input.onkeydown = () => false;
    ["input", "invalid", "change"].forEach((event) =>
      picker._input?.addEventListener(event, () => {
        showErrorMessage(field);
      })
    );
  }

  // Hide the field wrapper if field is hidden by conditional logic.
  const updateHiddenState = () => {
    if (
      !(field.wrapperElement instanceof HTMLElement) ||
      !(field.rootElement instanceof HTMLElement)
    ) {
      return;
    }

    const container = field.rootElement.closest<HTMLElement>(
      "[data-field-container]"
    );
    const isHidden = container ? "hidden" in container.dataset : false;

    field.wrapperElement.classList.toggle("hidden", isHidden);

    if (field.required) {
      const inputs: HTMLElement[] = ["date", "radio"].includes(field.type)
        ? [...field.rootElement.querySelectorAll("input")]
        : field.inputElement
        ? [field.inputElement]
        : [];

      if (isHidden) {
        inputs.forEach((input) => input.removeAttribute("required"));
      } else {
        inputs.forEach((input) => input.setAttribute("required", "required"));
      }
    }
  };

  const observeTarget =
    field.rootElement.closest<HTMLElement>("[data-field-container]") ??
    field.rootElement;

  new MutationObserver(updateHiddenState).observe(observeTarget, {
    attributes: true,
  });

  updateHiddenState();

  return field;
};

/** Email validator using RFC 5322. */
const showEmailValidity = (field: Field): Field => {
  field.inputElement?.setCustomValidity(
    field.inputElement.value.match(RFC_5322_VALIDATION) === null &&
      !(field.inputElement.required === false && !field.inputElement.value)
      ? translations["INVALID_EMAIL_ADDRESS"]
      : ""
  );

  return field;
};

/** Number min/max length validator. */
const showNumberLengthValidity = (field: Field): Field => {
  if (!field.inputElement) {
    return field;
  }

  if (
    field.inputElement.value.length < field.inputElement.minLength &&
    field.inputElement.getAttribute("minlength")
  ) {
    field.inputElement?.setCustomValidity(
      `${translations["INVALID_MIN_LENGTH"]} ${field.inputElement.minLength}`
    );
  } else if (field.inputElement.getAttribute("maxlength")) {
    field.inputElement?.setCustomValidity(
      field.inputElement.value.length > field.inputElement.maxLength
        ? `${translations["INVALID_MAX_LENGTH"]} ${field.inputElement.maxLength}`
        : ""
    );
  }

  return field;
};

/** Confirmation target validator. */
const showConfirmationValidity = (confirmationField: Field): void => {
  if (!confirmationField.inputElement) {
    return;
  }

  const targetField = fields().find(
    (field) =>
      field.inputElement?.name ===
      confirmationField.inputElement?.dataset.confirmationTarget
  );

  if (!targetField || !targetField.inputElement) {
    return;
  }

  confirmationField.inputElement?.setCustomValidity(
    confirmationField.inputElement.value !== targetField.inputElement.value
      ? translations["CONFIRMATION_FIELD_MUST_MATCH"]
      : ""
  );
};

/** Attach all validators for a field. */
const attachValidators = (field: Field): Field => {
  if (field.type === "email") {
    ["keydown", "input"].forEach((event) =>
      field.inputElement?.addEventListener(event, () =>
        showEmailValidity(field)
      )
    );
  }

  if (field.type === "number") {
    ["input"].forEach((event) =>
      field.inputElement?.addEventListener(event, () =>
        showNumberLengthValidity(field)
      )
    );
  }

  if (field.inputElement?.dataset.confirmationTarget !== undefined) {
    ["keydown", "input"].forEach((event) =>
      field.inputElement?.addEventListener(event, () =>
        showConfirmationValidity(field)
      )
    );
  }

  return field;
};

/** Find and hydrate each field. */
export const fields = (): Field[] =>
  Array.from(
    document.querySelectorAll(".js-field") as NodeListOf<HTMLElement>
  ).map((field): Field => {
    return {
      type: field.dataset.type || "",
      rootElement: field,
      inputElement: field.querySelector(".js-field-input"),
      required:
        field.querySelector(".js-field-input")?.hasAttribute("required") ||
        field.querySelector(".js-field-choice")?.hasAttribute("required") ||
        false,
      fakeInputElement: field.querySelector(".js-field-fake-input"),
      fakeValueElement: field.querySelector(".js-field-fake-value"),
      fakeOptionElements: () =>
        Array.from(field.querySelectorAll(".js-field-fake-option")),
      listbox: field.querySelector(".js-field-listbox"),
      errorElement: field.querySelector(".js-field-error"),
      wrapperElement: field.closest(".js-field-wrapper"),
    };
  });

export const setFieldOptions = (field: Field, options: Option[]) => {
  if (field.listbox === null) {
    throw new Error("Unsupported field: listbox not found.");
  }

  field.listbox.innerHTML = "";

  options.forEach((option) => {
    const li = document.createElement("li");
    li.innerText = option.label;
    li.dataset.value = option.value;
    li.setAttribute("role", "option");
    li.classList.add("field__dropdown-item", "js-field-fake-option");
    field.listbox?.append(li);
  });
  if (field.inputElement?.value && field.fakeValueElement) {
    const selectedOption = options.find(
      (option) => option.value === field.inputElement?.value
    );

    if (selectedOption) {
      field.fakeValueElement.innerText = selectedOption.label;
    }
  }
};

const initializeFields = (): Field[] =>
  fields().map(attachListeners).map(attachValidators);

window.addEventListener("htmx:afterSwap", initializeFields);

export default initializeFields();
